import React from 'react';
import type { SvgIconComponent } from '@mui/icons-material';
import {
  ChevronRight as ChevronRightIcon,
  DrawOutlined as DrawOutlinedIcon,
  EventAvailableOutlined as EventAvailableOutlinedIcon,
  HandymanOutlined as HandymanOutlinedIcon,
  ManageSearchOutlined as ManageSearchOutlinedIcon,
  RequestQuoteOutlined as RequestQuoteOutlinedIcon,
  VpnKeyOutlined as VpnKeyOutlinedIcon,
} from '@mui/icons-material';
import { useRevealOnScroll } from '../../hooks/useRevealOnScroll';

export type PurchaseStep = {
  id: string;
  step: number;
  title: string;
  description: string;
  icon: SvgIconComponent;
};

const DEFAULT_STEPS: PurchaseStep[] = [
  {
    id: 'decouvrir',
    step: 1,
    title: 'Découvrir',
    description: 'Parcourez nos programmes et échangez avec un conseiller pour affiner votre projet.',
    icon: ManageSearchOutlinedIcon,
  },
  {
    id: 'reserver',
    step: 2,
    title: 'Réserver',
    description: 'Choisissez votre lot ou appartement et déposez un acompte pour le bloquer.',
    icon: EventAvailableOutlinedIcon,
  },
  {
    id: 'financer',
    step: 3,
    title: 'Financer',
    description: 'Montez votre dossier bancaire avec l’aide de nos équipes et de votre courtier.',
    icon: RequestQuoteOutlinedIcon,
  },
  {
    id: 'signer',
    step: 4,
    title: 'Signer',
    description: 'Finalisez l’achat par le contrat de vente en VEFA, en présence du notaire.',
    icon: DrawOutlinedIcon,
  },
  {
    id: 'suivre',
    step: 5,
    title: 'Suivre',
    description: 'Pilotez l’avancement du chantier jusqu’à la livraison de votre bien.',
    icon: HandymanOutlinedIcon,
  },
  {
    id: 'habiter',
    step: 6,
    title: 'Habiter',
    description: 'Réceptionnez votre logement, recevez les clés et emménagez sereinement.',
    icon: VpnKeyOutlinedIcon,
  },
];

interface PurchaseGuideTimelineProps {
  className?: string;
  steps?: PurchaseStep[];
}

const HEX_PATH = 'M50 3 L93.3 27.5 V77.5 L50 102 L6.7 77.5 V27.5 Z';

const PurchaseStepBadge: React.FC<{ step: number }> = ({ step }) => (
  <span className="purchase-step-badge" aria-hidden>
    {String(step).padStart(2, '0')}
  </span>
);

const PurchaseStepHex: React.FC<{ icon: SvgIconComponent; className?: string }> = ({
  icon: Icon,
  className = '',
}) => (
  <span className={`purchase-step-hex ${className}`} aria-hidden>
    <svg className="purchase-step-hex__shape" viewBox="0 0 100 115" aria-hidden>
      <path d={HEX_PATH} />
    </svg>
    <Icon className="purchase-step-hex__icon relative z-[1] text-teal-500" />
  </span>
);

const PurchaseStepCopy: React.FC<{ title: string; description: string }> = ({
  title,
  description,
}) => (
  <>
    <h3 className="purchase-step-copy__title">{title}</h3>
    <p className="purchase-step-copy__desc">{description}</p>
  </>
);

const PurchaseGuideTimeline: React.FC<PurchaseGuideTimelineProps> = ({
  steps = DEFAULT_STEPS,
  className = '',
}) => {
  const { ref: headerRef, visible: headerVisible } = useRevealOnScroll<HTMLElement>();
  const { ref: timelineRef, visible: timelineVisible } = useRevealOnScroll<HTMLDivElement>();

  return (
    <section
      className={`purchase-guide-section relative scroll-mt-28 overflow-hidden bg-brand-charcoal py-12 md:scroll-mt-32 md:py-14 lg:pb-14 lg:pt-[5.25rem] ${className}`}
    >
      <div className="relative z-[1] mx-auto w-full max-w-[1440px] px-4 sm:px-5 md:px-6">
        <header
          ref={headerRef}
          className={`reveal-up mb-10 max-w-2xl md:mb-10 lg:mb-12 xl:mb-14 ${headerVisible ? 'reveal-up--visible' : ''}`}
        >
          <p className="eyebrow-accent mb-3 tracking-[0.18em] lg:mb-3.5">06 • VOTRE PARCOURS</p>
          <div
            className={`accent-dash reveal-line ${headerVisible ? 'reveal-line--visible' : ''}`}
            aria-hidden
          />
          <h2 className="headline-brand-section mt-5 max-w-2xl text-white lg:mt-6">
            Acheter dans le neuf,{' '}
            <span className="headline-brand-accent">étape par étape.</span>
          </h2>
          <p className="purchase-guide-lead mt-3 max-w-lg text-[15px] font-normal leading-[1.7] sm:mt-4 sm:text-base lg:mt-4 lg:text-[17px]">
            Un processus clair, encadré et accompagné, de la première visite à la remise des clés.
          </p>
        </header>

        <div
          ref={timelineRef}
          className={`reveal-up reveal-up-delay-1 ${timelineVisible ? 'reveal-up--visible' : ''}`}
        >
          {/* Desktop — timeline horizontale (badges, flèches, hexagones, textes) */}
          <div className="purchase-timeline-track hidden lg:block">
            <div className="purchase-timeline-rail" aria-hidden />

            <ol
              className={`reveal-stagger grid grid-cols-6 gap-0 ${timelineVisible ? 'reveal-stagger--active' : ''}`}
            >
              {steps.map(({ id, step, title, description, icon }, index) => (
                <li
                  key={id}
                  className="group flex cursor-pointer flex-col items-center px-2 text-center xl:px-3"
                >
                  <div className="purchase-timeline-step__head w-full">
                    <PurchaseStepBadge step={step} />
                    {index < steps.length - 1 ? (
                      <span className="purchase-timeline-arrow" aria-hidden>
                        <ChevronRightIcon className="purchase-timeline-arrow__icon" />
                      </span>
                    ) : null}
                  </div>

                  <PurchaseStepHex icon={icon} className="purchase-step-hex--desktop" />

                  <div className="mx-auto max-w-[10.75rem] xl:max-w-[11.75rem]">
                    <PurchaseStepCopy title={title} description={description} />
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* Tablette — grille 3×2 */}
          <ol
            className={`reveal-stagger hidden gap-x-6 gap-y-10 sm:grid sm:grid-cols-2 md:grid-cols-3 lg:hidden ${timelineVisible ? 'reveal-stagger--active' : ''}`}
          >
            {steps.map(({ id, step, title, description, icon }) => (
              <li
                key={id}
                className="group flex cursor-pointer flex-col items-center px-2 text-center"
              >
                <PurchaseStepBadge step={step} />
                <PurchaseStepHex icon={icon} className="my-2" />
                <div className="mx-auto max-w-[14rem]">
                  <PurchaseStepCopy title={title} description={description} />
                </div>
              </li>
            ))}
          </ol>

          {/* Mobile — timeline verticale */}
          <ol className="relative flex flex-col gap-0 sm:hidden">
            {steps.map(({ id, step, title, description, icon }, index) => (
              <li
                key={id}
                className="group relative flex cursor-pointer gap-5 pb-9 last:pb-0"
              >
                <div className="flex shrink-0 flex-col items-center">
                  <PurchaseStepBadge step={step} />
                  {index < steps.length - 1 ? (
                    <span className="purchase-timeline-mobile-rail" aria-hidden />
                  ) : null}
                </div>
                <div className="min-w-0 pt-0.5">
                  <PurchaseStepHex icon={icon} className="purchase-step-hex--compact mb-1.5" />
                  <PurchaseStepCopy title={title} description={description} />
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};

export default PurchaseGuideTimeline;
